import React, { useMemo, useState } from 'react';
import {
  createPostgresConnection,
  proposePostgresPlanes,
  setupPostgresPlanes,
  testPostgresConnectionPayload,
} from '../utils/api';
import './PostgresIngestWizard.css';

const STEPS = ['connect', 'primer', 'review', 'done'];

function defaultForm() {
  return {
    name: '',
    connection_url: '',
    host: '',
    port: 5432,
    database_name: '',
    username: '',
    password: '',
    ssl_mode: 'prefer',
    primer_markdown: '',
  };
}

export default function PostgresIngestWizard({ open, suffix, onClose, onComplete }) {
  const [step, setStep] = useState('connect');
  const [form, setForm] = useState(defaultForm);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [connectionId, setConnectionId] = useState('');
  const [reasoning, setReasoning] = useState('');
  const [proposals, setProposals] = useState([]);
  const [setupResult, setSetupResult] = useState(null);

  const patch = (p) => setForm((prev) => ({ ...prev, ...p }));

  const canConnect = useMemo(() => {
    if (!form.name.trim()) return false;
    if (form.connection_url.trim()) return true;
    return !!(form.host.trim() && form.database_name.trim() && form.username.trim());
  }, [form]);

  const reset = () => {
    setStep('connect');
    setForm(defaultForm());
    setError('');
    setConnectionId('');
    setReasoning('');
    setProposals([]);
    setSetupResult(null);
  };

  const handleClose = () => {
    reset();
    onClose?.();
  };

  const handleTestAndSave = async () => {
    setLoading(true);
    setError('');
    try {
      await testPostgresConnectionPayload(suffix, form);
      const created = await createPostgresConnection(suffix, {
        name: form.name.trim(),
        connection_url: form.connection_url.trim() || undefined,
        host: form.host.trim() || undefined,
        port: form.port,
        database_name: form.database_name.trim() || undefined,
        username: form.username.trim() || undefined,
        password: form.password || undefined,
        ssl_mode: form.ssl_mode,
        primer_markdown: form.primer_markdown || null,
      });
      setConnectionId(created.data.id);
      setStep('primer');
    } catch (err) {
      setError(err.message || 'Connection failed');
    } finally {
      setLoading(false);
    }
  };

  const handlePropose = async () => {
    if (!connectionId) return;
    if (!form.primer_markdown.trim()) {
      setError('Paste a schema primer (markdown) before proposing planes');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await proposePostgresPlanes(suffix, connectionId, {
        primer_markdown: form.primer_markdown,
      });
      const tables = (res.data?.tables || []).map((t) => ({
        ...t,
        include: t.include_default !== false,
        group_slug: t.suggested_slug,
        plane_name: t.name || t.table,
        existing_group_slug: '',
      }));
      setReasoning(res.data?.reasoning || '');
      setProposals(tables);
      setStep('review');
    } catch (err) {
      setError(err.message || 'Propose failed');
    } finally {
      setLoading(false);
    }
  };

  const updateProposal = (index, patchObj) => {
    setProposals((prev) => prev.map((row, i) => (i === index ? { ...row, ...patchObj } : row)));
  };

  const handleSetup = async () => {
    setLoading(true);
    setError('');
    try {
      const tables = proposals.map((p) => ({
        include: !!p.include,
        schema: p.schema || 'public',
        table: p.table,
        group_slug: p.group_slug,
        name: p.plane_name,
        existing_group_slug: p.existing_group_slug || undefined,
        unique_key_columns: p.unique_key_columns,
        watermark_column: p.watermark_column || null,
        column_mapping: p.column_mapping || {},
      }));
      const res = await setupPostgresPlanes(suffix, connectionId, { tables });
      setSetupResult(res.data);
      setStep('done');
      onComplete?.(res.data);
    } catch (err) {
      setError(err.message || 'Setup failed');
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="postgres-ingest-overlay" role="presentation">
      <div className="postgres-ingest-modal" role="dialog" aria-modal="true" aria-labelledby="pg-ingest-title">
        <div className="postgres-ingest-header">
          <h3 id="pg-ingest-title">Ingest from database</h3>
          <p className="postgres-ingest-hint">
            Connect to Postgres, provide a schema primer, choose source tables, and create one ingestion plane per table.
          </p>
          <div className="postgres-ingest-steps">
            {STEPS.map((s) => (
              <span key={s} className={`postgres-ingest-step${step === s ? ' active' : ''}`}>{s}</span>
            ))}
          </div>
        </div>

        <div className="postgres-ingest-body">
          {error && <p className="postgres-ingest-error">{error}</p>}
          {loading && <p><i className="fas fa-spinner fa-spin" /> Working…</p>}

          {step === 'connect' && !loading && (
            <>
              <div className="form-row">
                <label>Connection name</label>
                <input className="form-control" value={form.name} onChange={(e) => patch({ name: e.target.value })} placeholder="Innfall production" />
              </div>
              <div className="form-row">
                <label>Connection URL (optional)</label>
                <input className="form-control" value={form.connection_url} onChange={(e) => patch({ connection_url: e.target.value })} placeholder="postgres://user:pass@host:5432/db" />
              </div>
              <p className="postgres-ingest-hint">Or fill host fields below (used when URL is empty):</p>
              <div className="postgres-ingest-grid">
                <div className="form-row">
                  <label>Host</label>
                  <input className="form-control" value={form.host} onChange={(e) => patch({ host: e.target.value })} />
                </div>
                <div className="form-row">
                  <label>Port</label>
                  <input className="form-control" type="number" value={form.port} onChange={(e) => patch({ port: Number(e.target.value) || 5432 })} />
                </div>
                <div className="form-row">
                  <label>Database</label>
                  <input className="form-control" value={form.database_name} onChange={(e) => patch({ database_name: e.target.value })} />
                </div>
                <div className="form-row">
                  <label>Username</label>
                  <input className="form-control" value={form.username} onChange={(e) => patch({ username: e.target.value })} />
                </div>
                <div className="form-row">
                  <label>Password</label>
                  <input className="form-control" type="password" value={form.password} onChange={(e) => patch({ password: e.target.value })} autoComplete="new-password" />
                </div>
                <div className="form-row">
                  <label>SSL mode</label>
                  <select className="form-control" value={form.ssl_mode} onChange={(e) => patch({ ssl_mode: e.target.value })}>
                    <option value="prefer">prefer</option>
                    <option value="require">require</option>
                    <option value="disable">disable</option>
                    <option value="verify-full">verify-full</option>
                  </select>
                </div>
              </div>
            </>
          )}

          {step === 'primer' && !loading && (
            <div className="form-row">
              <label htmlFor="pg-primer">Schema primer (markdown)</label>
              <textarea
                id="pg-primer"
                className="form-control postgres-ingest-primer"
                value={form.primer_markdown}
                onChange={(e) => patch({ primer_markdown: e.target.value })}
                placeholder="Paste the database summary markdown (tables, ER diagram, purpose)…"
              />
              <div className="postgres-ingest-hint">
                The model uses this primer plus live table introspection to suggest planes. Join tables default to off.
              </div>
            </div>
          )}

          {step === 'review' && !loading && (
            <>
              {reasoning && (
                <p className="postgres-ingest-hint"><strong>Plan:</strong> {reasoning}</p>
              )}
              <div className="postgres-ingest-table-list">
                {proposals.map((p, idx) => (
                  <label key={`${p.schema}.${p.table}`} className={`postgres-ingest-table-item${p.include ? ' selected' : ''}`}>
                    <input
                      type="checkbox"
                      checked={!!p.include}
                      onChange={(e) => updateProposal(idx, { include: e.target.checked })}
                    />
                    <div className="postgres-ingest-table-body">
                      <div className="postgres-ingest-table-title">
                        <strong>{p.schema}.{p.table}</strong>
                        {p.is_join_table ? <span className="postgres-ingest-badge">join</span> : null}
                      </div>
                      <div className="postgres-ingest-hint">{p.purpose}</div>
                      {p.include && (
                        <div className="postgres-ingest-plane-fields">
                          <input
                            className="form-control"
                            value={p.plane_name}
                            onChange={(e) => updateProposal(idx, { plane_name: e.target.value })}
                            placeholder="Plane name"
                          />
                          <input
                            className="form-control"
                            value={p.group_slug}
                            onChange={(e) => updateProposal(idx, { group_slug: e.target.value })}
                            placeholder="group_slug"
                          />
                          <input
                            className="form-control"
                            value={p.watermark_column || ''}
                            onChange={(e) => updateProposal(idx, { watermark_column: e.target.value || null })}
                            placeholder="watermark column"
                          />
                        </div>
                      )}
                    </div>
                  </label>
                ))}
              </div>
            </>
          )}

          {step === 'done' && setupResult && (
            <div>
              <p><strong>Planes created:</strong> {(setupResult.created || []).length}</p>
              <ul>
                {(setupResult.created || []).map((c) => (
                  <li key={c.group_slug}>{c.group_slug} ← {c.schema}.{c.table}</li>
                ))}
              </ul>
              {(setupResult.updated || []).length > 0 && (
                <>
                  <p><strong>Updated:</strong></p>
                  <ul>
                    {setupResult.updated.map((c) => (
                      <li key={c.group_slug}>{c.group_slug}</li>
                    ))}
                  </ul>
                </>
              )}
              {(setupResult.errors || []).length > 0 && (
                <>
                  <p className="postgres-ingest-error"><strong>Errors:</strong></p>
                  <ul>
                    {setupResult.errors.map((e, i) => (
                      <li key={i}>{e.table || e.group_slug}: {e.error}</li>
                    ))}
                  </ul>
                </>
              )}
              <p className="postgres-ingest-hint">
                Run ingest from Horizon Data Ingestion (or schedule) on each plane. Watermarked tables pull deltas on subsequent runs.
              </p>
            </div>
          )}
        </div>

        <div className="postgres-ingest-footer">
          <button type="button" className="btn ghost" onClick={handleClose} disabled={loading}>
            {step === 'done' ? 'Close' : 'Cancel'}
          </button>
          {step === 'connect' && (
            <button type="button" className="btn" disabled={loading || !canConnect} onClick={handleTestAndSave}>
              Test &amp; continue
            </button>
          )}
          {step === 'primer' && (
            <>
              <button type="button" className="btn ghost" disabled={loading} onClick={() => setStep('connect')}>Back</button>
              <button type="button" className="btn" disabled={loading || !form.primer_markdown.trim()} onClick={handlePropose}>
                Propose planes
              </button>
            </>
          )}
          {step === 'review' && (
            <>
              <button type="button" className="btn ghost" disabled={loading} onClick={() => setStep('primer')}>Back</button>
              <button
                type="button"
                className="btn"
                disabled={loading || !proposals.some((p) => p.include)}
                onClick={handleSetup}
              >
                Create planes
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
